import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Activity, Zap, Navigation, TrendingUp, BarChart3, ChevronDown } from 'lucide-react';
import { Card } from '../../ui/Card';

interface GPSDataPoint {
  time: number;
  latitude: number;
  longitude: number;
  speed: number;
  distance: number;
}

interface PowerDataPoint {
  interval: string;
  [rower: string]: number | string;
}

interface SessionData {
  averagePower?: number;
  strokeRate?: number;
  totalDistance?: number;
  efficiency?: number;
  powerTrend?: number;
  strokeRateTrend?: number;
  distanceTrend?: number;
  efficiencyTrend?: number;
}

interface OverviewCardsProps {
  powerData: PowerDataPoint[];
  gpsData: GPSDataPoint[];
  sessionData: SessionData | null;
  loading: boolean;
  strokeRateLoading?: boolean;
  efficiencyLoading?: boolean;
  powerLoading?: boolean;
  gpsLoading?: boolean;
  disableCollapse?: boolean; // Disable collapse functionality (e.g., in dashboard grid)
}

const OverviewCards: React.FC<OverviewCardsProps> = ({
  powerData,
  gpsData,
  sessionData,
  strokeRateLoading = false,
  efficiencyLoading = false,
  powerLoading = false,
  gpsLoading = false,
  disableCollapse = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true); // Default expanded
  // Always expanded when collapse is disabled
  const effectiveIsExpanded = disableCollapse ? true : isExpanded;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);

  // Track container width for responsive sizing
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate metrics from data if not provided in sessionData
  const calculateAveragePower = (): number => {
    if (sessionData?.averagePower) return sessionData.averagePower;

    if (powerData.length === 0) return 0;

    let totalPower = 0;
    let count = 0;

    powerData.forEach((interval) => {
      Object.entries(interval).forEach(([key, value]) => {
        if (key !== 'interval' && typeof value === 'number') {
          totalPower += value;
          count++;
        }
      });
    });

    return count > 0 ? Math.round(totalPower / count) : 0;
  };

  const calculateDistance = (): number => {
    if (sessionData?.totalDistance) return sessionData.totalDistance;

    if (gpsData.length === 0) return 0;

    const lastPoint = gpsData[gpsData.length - 1];
    return lastPoint ? lastPoint.distance : 0;
  };

  const calculateStrokeRate = (): number => {
    if (sessionData?.strokeRate) return sessionData.strokeRate;
    return 0; // Return 0 to show "--" when no data
  };

  const formatTrend = (
    trend: number | undefined,
    type: 'percentage' | 'distance' | 'neutral'
  ): string => {
    if (trend === undefined) return '';

    if (trend === 0) return '';

    const symbol = trend > 0 ? '↑' : '↓';

    switch (type) {
      case 'percentage':
        return `${symbol} ${Math.abs(trend)}% vs last session`;
      case 'distance':
        return `${symbol} ${Math.abs(trend)}m vs target`;
      default:
        return `${symbol} ${Math.abs(trend)}% improvement`;
    }
  };

  const averagePower = calculateAveragePower();
  const strokeRate = calculateStrokeRate();
  const distance = calculateDistance();
  // const efficiency = calculateEfficiency();

  // Calculate responsive styles based on container width
  const responsiveStyles = useMemo(() => {
    if (containerWidth < 400) {
      return {
        contentPadding: 'px-2 pb-3',
        gridGap: 'gap-2',
        cardPadding: 'p-1.5',
        labelFont: 'text-xs',
        labelMargin: 'mb-1.5',
        valueFont: 'text-lg',
        subtitleFont: 'text-[0.625rem]',
        iconSize: 12,
        iconGap: 'gap-1',
        headerPadding: 'p-3',
        headerTitleFont: 'text-base',
        headerSubtitleFont: 'text-xs',
      };
    }
    if (containerWidth < 600) {
      return {
        contentPadding: 'px-2.5 pb-4',
        gridGap: 'gap-2.5',
        cardPadding: 'p-2',
        labelFont: 'text-xs',
        labelMargin: 'mb-2',
        valueFont: 'text-xl',
        subtitleFont: 'text-xs',
        iconSize: 13,
        iconGap: 'gap-1.5',
        headerPadding: 'p-3',
        headerTitleFont: 'text-base',
        headerSubtitleFont: 'text-xs',
      };
    }
    if (containerWidth < 800) {
      return {
        contentPadding: 'px-3 pb-5',
        gridGap: 'gap-3',
        cardPadding: 'p-2.5',
        labelFont: 'text-xs',
        labelMargin: 'mb-2',
        valueFont: 'text-xl',
        subtitleFont: 'text-xs',
        iconSize: 14,
        iconGap: 'gap-2',
        headerPadding: 'p-4',
        headerTitleFont: 'text-lg',
        headerSubtitleFont: 'text-sm',
      };
    }
    // Default for larger containers
    return {
      contentPadding: 'px-4 pb-6',
      gridGap: 'gap-3 sm:gap-4',
      cardPadding: 'p-2 sm:p-4',
      labelFont: 'text-xs sm:text-sm',
      labelMargin: 'mb-2',
      valueFont: 'text-xl sm:text-2xl',
      subtitleFont: 'text-xs',
      iconSize: 14,
      iconGap: 'gap-2',
      headerPadding: 'p-4',
      headerTitleFont: 'text-lg',
      headerSubtitleFont: 'text-sm',
    };
  }, [containerWidth]);

  // Check if we have any real data to display (Overview Cards need Aperiodic data)
  const hasAnyData = averagePower > 0 || strokeRate > 0 || distance > 0;

  // Check if we're still loading any data
  const isStillLoading = powerLoading || gpsLoading || strokeRateLoading || efficiencyLoading;

  // Show combined loading if we're still loading AND no data yet
  const showCombinedLoading = isStillLoading && !hasAnyData;

  // Show combined loading animation until we get at least one piece of data
  if (showCombinedLoading) {
    return (
      <div ref={containerRef} className="bg-white border border-gray-200 rounded-lg mb-6">
        <div
          className={`${responsiveStyles.headerPadding} ${!disableCollapse ? 'cursor-pointer' : ''}`}
          onClick={!disableCollapse ? () => setIsExpanded(!isExpanded) : undefined}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h3 className={`${responsiveStyles.headerTitleFont} font-semibold text-gray-900`}>
                Session Overview
              </h3>
              {!disableCollapse && (
                <ChevronDown
                  className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                    isExpanded ? 'transform rotate-180' : ''
                  }`}
                />
              )}
            </div>
            <div className={responsiveStyles.headerSubtitleFont + ' text-gray-600'}>
              High-level performance metrics
            </div>
          </div>
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            effectiveIsExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className={responsiveStyles.contentPadding}>
            <div
              className={`overview-grid grid ${responsiveStyles.gridGap}`}
              style={{
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              }}
              data-testid="overview-cards-loading"
            >
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <Card.Body className={responsiveStyles.cardPadding}>
                    <div className="animate-pulse">
                      <div className="h-4 bg-rowing-gray-200 rounded mb-2"></div>
                      <div className="h-8 bg-rowing-gray-200 rounded mb-1"></div>
                      <div className="h-3 bg-rowing-gray-200 rounded"></div>
                      <div
                        className={`text-center text-rowing-gray-500 ${responsiveStyles.subtitleFont} mt-2`}
                      >
                        Loading...
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Only show "No performance data available" if we've finished loading and have no data
  if (!hasAnyData && !isStillLoading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <div className="text-gray-400 mb-2">
          <BarChart3 className="h-8 w-8 mx-auto" />
        </div>
        <p className="text-gray-600 text-sm">No performance data available for this session</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-white border border-gray-200 rounded-lg mb-6">
      {/* Collapsible Header */}
      <div
        className={`${responsiveStyles.headerPadding} ${!disableCollapse ? 'cursor-pointer' : ''}`}
        onClick={!disableCollapse ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className={`${responsiveStyles.headerTitleFont} font-semibold text-gray-900`}>
              Session Overview
            </h3>
            {!disableCollapse && (
              <ChevronDown
                className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                  isExpanded ? 'transform rotate-180' : ''
                }`}
              />
            )}
          </div>
          <div className={responsiveStyles.headerSubtitleFont + ' text-gray-600'}>
            High-level performance metrics
          </div>
        </div>
      </div>
      {/* Collapsible Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          effectiveIsExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className={responsiveStyles.contentPadding}>
          <div
            className={`overview-grid grid ${responsiveStyles.gridGap}`}
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            }}
            data-testid="overview-cards"
          >
            {/* Average Power Card */}
            <div data-testid="power-metric-card" className="min-w-0">
              <Card variant="interactive" className="h-full">
                <Card.Body className={responsiveStyles.cardPadding}>
                  <div
                    className={`flex items-center ${responsiveStyles.iconGap} text-rowing-gray-600 ${responsiveStyles.labelFont} font-medium ${responsiveStyles.labelMargin}`}
                  >
                    <Activity size={responsiveStyles.iconSize} className="flex-shrink-0" />
                    <span className="truncate">Average Power</span>
                  </div>
                  <div className={`${responsiveStyles.valueFont} font-bold text-rowing-navy`}>
                    {powerLoading ? (
                      <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                    ) : (
                      <>
                        {averagePower > 0 ? averagePower : '--'}
                        {averagePower > 0 && (
                          <span
                            className={`${responsiveStyles.subtitleFont} font-normal ml-1 text-rowing-gray-500`}
                          >
                            W
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className={`${responsiveStyles.subtitleFont} text-rowing-gray-500 mt-1`}>
                    per rower
                  </div>
                  {sessionData?.powerTrend !== undefined && (
                    <div
                      className={`text-xs mt-2 font-medium ${
                        sessionData.powerTrend > 0
                          ? 'text-green-600'
                          : sessionData.powerTrend < 0
                            ? 'text-rowing-red'
                            : 'text-rowing-gray-500'
                      }`}
                    >
                      {formatTrend(sessionData.powerTrend, 'percentage')}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>

            {/* Stroke Rate Card */}
            <div data-testid="stroke-rate-card" className="min-w-0">
              <Card variant="interactive" className="h-full">
                <Card.Body className={responsiveStyles.cardPadding}>
                  <div
                    className={`flex items-center ${responsiveStyles.iconGap} text-rowing-gray-600 ${responsiveStyles.labelFont} font-medium ${responsiveStyles.labelMargin}`}
                  >
                    <Zap size={responsiveStyles.iconSize} className="flex-shrink-0" />
                    <span className="truncate">Stroke Rate</span>
                  </div>
                  <div className={`${responsiveStyles.valueFont} font-bold text-rowing-navy`}>
                    {strokeRateLoading ? (
                      <div className="animate-pulse h-8 bg-rowing-gray-200 rounded w-12"></div>
                    ) : strokeRate > 0 ? (
                      strokeRate
                    ) : (
                      '--'
                    )}
                  </div>
                  <div className={`${responsiveStyles.subtitleFont} text-rowing-gray-500 mt-1`}>
                    strokes/min avg
                  </div>
                  {sessionData?.strokeRateTrend !== undefined && (
                    <div
                      className={`text-xs mt-2 font-medium ${
                        sessionData.strokeRateTrend === 0
                          ? 'text-rowing-gray-500'
                          : 'text-green-600'
                      }`}
                    >
                      {formatTrend(sessionData.strokeRateTrend, 'neutral')}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>

            {/* Distance Card */}
            <div data-testid="distance-card" className="min-w-0">
              <Card variant="interactive" className="h-full">
                <Card.Body className={responsiveStyles.cardPadding}>
                  <div
                    className={`flex items-center ${responsiveStyles.iconGap} text-rowing-gray-600 ${responsiveStyles.labelFont} font-medium ${responsiveStyles.labelMargin}`}
                  >
                    <Navigation size={responsiveStyles.iconSize} className="flex-shrink-0" />
                    <span className="truncate">Distance</span>
                  </div>
                  <div className={`${responsiveStyles.valueFont} font-bold text-rowing-navy`}>
                    {gpsLoading ? (
                      <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                    ) : distance > 0 ? (
                      `${(distance / 1000).toFixed(1)}km`
                    ) : (
                      '--'
                    )}
                  </div>
                  <div className={`${responsiveStyles.subtitleFont} text-rowing-gray-500 mt-1`}>
                    total course
                  </div>
                  {sessionData?.distanceTrend !== undefined && (
                    <div
                      className={`text-xs mt-2 font-medium ${
                        sessionData.distanceTrend > 0
                          ? 'text-green-600'
                          : sessionData.distanceTrend < 0
                            ? 'text-rowing-red'
                            : 'text-rowing-gray-500'
                      }`}
                    >
                      {formatTrend(sessionData.distanceTrend, 'distance')}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>

            {/* Efficiency Card */}
            <div data-testid="efficiency-card" className="min-w-0">
              <Card variant="interactive" className="h-full">
                <Card.Body className={responsiveStyles.cardPadding}>
                  <div
                    className={`flex items-center ${responsiveStyles.iconGap} text-rowing-gray-600 ${responsiveStyles.labelFont} font-medium ${responsiveStyles.labelMargin}`}
                  >
                    <TrendingUp size={responsiveStyles.iconSize} className="flex-shrink-0" />
                    <span className="truncate">Efficiency</span>
                  </div>
                  <div
                    className={`${responsiveStyles.valueFont} font-semibold text-rowing-gray-500`}
                  >
                    {efficiencyLoading ? (
                      <div className="animate-pulse h-8 bg-rowing-gray-200 rounded w-12"></div>
                    ) : (
                      'Coming Soon'
                    )}
                  </div>
                  <div className={`${responsiveStyles.subtitleFont} text-rowing-gray-500 mt-1`}>
                    enhanced analytics
                  </div>
                  {sessionData?.efficiencyTrend !== undefined && (
                    <div
                      className={`text-xs mt-2 font-medium ${
                        sessionData.efficiencyTrend > 0
                          ? 'text-green-600'
                          : sessionData.efficiencyTrend < 0
                            ? 'text-rowing-red'
                            : 'text-rowing-gray-500'
                      }`}
                    >
                      {formatTrend(sessionData.efficiencyTrend, 'percentage')}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewCards;
